import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Stamp, MapPin, Camera, Check, X } from "lucide-react";
import { toast } from "sonner";

// Resize/compress camera capture to keep payload under ~150KB
async function compressImage(file, maxDim = 1024, quality = 0.7) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Failed to read photo"));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("Failed to decode photo"));
      img.onload = () => {
        const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

export default function CheckInDialog({ business, onCheckedIn, trigger }) {
  const [open, setOpen] = useState(false);
  const [stage, setStage] = useState("idle"); // idle | locating | located | submitting | done
  const [coords, setCoords] = useState(null);
  const [photo, setPhoto] = useState(null); // data URL
  const [error, setError] = useState("");
  const [my, setMy] = useState({ visits: 0, next_at: null });
  const [reward, setReward] = useState(null);
  const [photoPrompt, setPhotoPrompt] = useState("Include your receipt or the product you just bought from this business.");
  const fileRef = useRef(null);

  // Load progress when dialog opens
  useEffect(() => {
    if (!open || !business?.id) return;
    api.get(`/businesses/${business.id}/loyalty`).then(({ data }) => {
      setMy({ visits: data.my_visits || 0, next_at: data.my_next_checkin_at });
      setPhotoPrompt(data?.config?.photo_prompt || "Include your receipt or the product you just bought from this business.");
    }).catch(() => {});
    setStage("idle");
    setCoords(null);
    setPhoto(null);
    setError("");
    setReward(null);
  }, [open, business?.id]);

  const requestGps = () => {
    if (!navigator.geolocation) {
      setError("Your browser doesn't support GPS. Try a different browser.");
      return;
    }
    setStage("locating");
    setError("");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude, acc: pos.coords.accuracy });
        setStage("located");
      },
      (err) => {
        setStage("idle");
        const msg = err.code === 1
          ? "Location permission denied. Enable location in your browser settings to check in."
          : err.code === 2
          ? "Couldn't determine your location. Try moving outdoors and retry."
          : "Location request timed out. Try again.";
        setError(msg);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const dataUrl = await compressImage(file);
      setPhoto(dataUrl);
    } catch (err) {
      toast.error(err.message || "Couldn't process photo. Try again.");
    } finally {
      // reset so re-selecting the same image still fires onChange
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const submit = async () => {
    if (!coords) { setError("Capture your GPS location first."); return; }
    if (!photo) { setError("Take a check-in photo first."); return; }
    setStage("submitting");
    setError("");
    try {
      const { data } = await api.post(`/businesses/${business.id}/checkin`, {
        lat: coords.lat,
        lng: coords.lng,
        photo_data_url: photo,
        fresh: true,
      });
      setStage("done");
      setReward({
        visits: data.visits,
        required: data.visits_required,
        just_earned: data.just_earned_reward,
        reward_text: data.reward_text,
      });
      toast.success(data.just_earned_reward ? "Stamp card complete — reward earned!" : "Check-in recorded");
      onCheckedIn?.(data);
    } catch (err) {
      setStage("located");
      const detail = formatApiError(err.response?.data?.detail) || err.message;
      setError(detail);
    }
  };

  const onCooldown = !!my.next_at && new Date(my.next_at) > new Date();

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {trigger ? (
        <span onClick={() => setOpen(true)} className="inline-block">{trigger}</span>
      ) : (
        <Button
          onClick={() => setOpen(true)}
          className="rounded-[35px] shadow-sm hover:shadow-md bg-[#B31E1E] hover:bg-[#8F1717]"
          data-testid="checkin-open-button"
        >
          <Stamp size={14} className="mr-1.5" /> Check in for a stamp
        </Button>
      )}

      <DialogContent className="rounded-md max-w-md" data-testid="checkin-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Check in at {business?.name}</DialogTitle>
          <DialogDescription>
            We'll verify your location and a fresh photo. One check-in per business per 24 hours.
          </DialogDescription>
        </DialogHeader>

        {/* Progress hint */}
        {my.visits > 0 && (
          <p className="text-sm text-gray-600" data-testid="checkin-progress">
            You currently have <strong>{my.visits}</strong> stamp{my.visits === 1 ? "" : "s"} with this business.
          </p>
        )}

        {onCooldown && stage !== "done" && (
          <div className="border border-amber-200 bg-amber-50 rounded-md p-3 text-sm text-amber-900" data-testid="checkin-cooldown">
            You've already checked in today. Next check-in available {new Date(my.next_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}.
          </div>
        )}

        {stage === "done" && reward && (
          <div className="border border-emerald-300 bg-emerald-50 rounded-md p-4" data-testid="checkin-success">
            <div className="flex items-center gap-2">
              <Check size={18} className="text-emerald-700" />
              <p className="font-medium text-emerald-900">Stamp earned</p>
            </div>
            <p className="text-sm text-emerald-900 mt-2">
              You now have <strong>{reward.visits}</strong> stamp{reward.visits === 1 ? "" : "s"} with {business?.name}.
            </p>
            {reward.just_earned && reward.reward_text && (
              <div className="mt-3 border-t border-emerald-200 pt-3">
                <p className="text-[11px] font-mono uppercase tracking-wider text-emerald-800">Reward unlocked</p>
                <p className="text-sm text-emerald-900 mt-1">{reward.reward_text}</p>
              </div>
            )}
          </div>
        )}

        {stage !== "done" && !onCooldown && (
          <div className="space-y-4">
            {/* GPS step */}
            <div className="border border-border rounded-md p-3" data-testid="checkin-gps-step">
              <div className="flex items-center gap-2">
                <MapPin size={16} className={coords ? "text-emerald-600" : "text-gray-400"} />
                <p className="text-sm font-medium">1. Share your location</p>
                {coords && <Check size={14} className="text-emerald-600 ml-auto" />}
              </div>
              {!coords ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={requestGps}
                  disabled={stage === "locating"}
                  className="mt-2 rounded-[35px]"
                  data-testid="checkin-gps-button"
                >
                  {stage === "locating" ? "Locating…" : "Use my current location"}
                </Button>
              ) : (
                <p className="text-[11px] font-mono text-gray-500 mt-2">
                  Lat {coords.lat.toFixed(5)}, Lng {coords.lng.toFixed(5)} (±{Math.round(coords.acc)}m)
                </p>
              )}
            </div>

            {/* Photo step */}
            <div className="border border-border rounded-md p-3" data-testid="checkin-photo-step">
              <div className="flex items-center gap-2">
                <Camera size={16} className={photo ? "text-emerald-600" : "text-gray-400"} />
                <p className="text-sm font-medium">2. Take a check-in photo</p>
                {photo && <Check size={14} className="text-emerald-600 ml-auto" />}
              </div>

              {/* Business-defined photo requirement */}
              <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2" data-testid="checkin-photo-prompt">
                <p className="text-[11px] font-mono uppercase tracking-wider text-amber-800">What to capture</p>
                <p className="text-sm text-amber-900 mt-0.5 leading-snug">{photoPrompt}</p>
              </div>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handlePhotoChange}
                className="hidden"
                data-testid="checkin-photo-input"
              />
              {!photo ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileRef.current?.click()}
                  disabled={!coords}
                  className="mt-2 rounded-[35px]"
                  data-testid="checkin-photo-button"
                >
                  Open camera
                </Button>
              ) : (
                <div className="mt-2 flex items-center gap-3">
                  <img src={photo} alt="Check-in capture" className="h-16 w-16 rounded-md border border-border object-cover" data-testid="checkin-photo-preview" />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setPhoto(null)}
                    className="rounded-[35px] text-red-600 hover:bg-red-50"
                    data-testid="checkin-photo-clear"
                  >
                    <X size={14} className="mr-1" /> Retake
                  </Button>
                </div>
              )}
              <p className="text-[11px] text-gray-500 mt-2">Camera capture only — gallery uploads are blocked to prevent fraud.</p>
            </div>

            {error && (
              <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="checkin-error">{error}</p>
            )}
          </div>
        )}

        <DialogFooter className="gap-2 flex-wrap">
          {stage === "done" ? (
            <Button onClick={() => setOpen(false)} className="rounded-[35px] bg-[#B31E1E] hover:bg-[#8F1717]" data-testid="checkin-close-button">Done</Button>
          ) : (
            <>
              <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-[35px]">Cancel</Button>
              {!onCooldown && (
                <Button
                  onClick={submit}
                  disabled={!coords || !photo || stage === "submitting"}
                  className="rounded-[35px] bg-[#B31E1E] hover:bg-[#8F1717] shadow-sm hover:shadow-md"
                  data-testid="checkin-submit-button"
                >
                  {stage === "submitting" ? "Checking in…" : "Check in"}
                </Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
